import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button, Card, Title, Text } from "@tremor/react";
import { toast } from "sonner";
import { Server as ServerIcon, Cpu, MemoryStick, HardDrive, Activity } from "lucide-react";
import {
  createServerToken,
  fetchServerTokens,
  fetchServers,
  fetchServerAlertRules,
  deleteServerAlertRule,
  revokeServerToken,
} from "../api/servers.js";

function fmtPct(v) {
  return v == null ? "—" : `${Math.round(v)}%`;
}

function fmtUptime(secs) {
  if (secs == null) return null;
  const d = Math.floor(secs / 86400);
  const h = Math.floor((secs % 86400) / 3600);
  const m = Math.floor((secs % 3600) / 60);
  if (d > 0) return `up ${d}d ${h}h`;
  if (h > 0) return `up ${h}h ${m}m`;
  return `up ${m}m`;
}

function Bar({ value, tone = "teal" }) {
  const pct = Math.max(0, Math.min(100, value ?? 0));
  const color =
    pct >= 90 ? "bg-red-500" : pct >= 75 ? "bg-amber-500" : `bg-${tone}-500`;
  return (
    <div className="h-1.5 w-full rounded bg-gray-100">
      <div className={`h-1.5 rounded ${color}`} style={{ width: `${pct}%` }} />
    </div>
  );
}

function relTime(iso) {
  if (!iso) return "never";
  const secs = (Date.now() - new Date(iso).getTime()) / 1000;
  if (secs < 90) return "just now";
  if (secs < 3600) return `${Math.round(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.round(secs / 3600)}h ago`;
  return `${Math.round(secs / 86400)}d ago`;
}

function isOnline(iso) {
  if (!iso) return false;
  return Date.now() - new Date(iso).getTime() < 2 * 60 * 1000;
}

function InstallPanel({ tokens, onChange }) {
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [newToken, setNewToken] = useState(null);

  async function create() {
    if (!name.trim()) return;
    setCreating(true);
    try {
      const t = await createServerToken(name.trim());
      setNewToken(t.token);
      setName("");
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.error || "Failed to create token");
    } finally {
      setCreating(false);
    }
  }

  async function revoke(id) {
    if (!confirm("Revoke this token? New servers can no longer enroll with it.")) return;
    try {
      await revokeServerToken(id);
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.error || "Revoke failed");
    }
  }

  const base = window.location.origin.replace(/^https?:\/\/[^.]+\./, "https://api.");
  const installCmd = newToken
    ? `curl -fsSL ${base}/install-agent.sh | sh -s -- --token ${newToken}`
    : `curl -fsSL ${base}/install-agent.sh | sh -s -- --token msrv_YOUR_TOKEN`;

  return (
    <Card>
      <Title>Install the agent</Title>
      <Text className="mt-1">
        Run this on any Linux VPS. Outbound-only — no inbound firewall changes needed.
      </Text>
      <div className="relative mt-3">
        <pre className="overflow-x-auto whitespace-pre-wrap break-all rounded bg-gray-900 p-3 font-mono text-xs text-gray-100">
          {installCmd}
        </pre>
        <button
          type="button"
          className="absolute right-2 top-2 rounded bg-gray-700 px-2 py-0.5 text-[10px] text-gray-300 hover:bg-gray-600"
          onClick={() => { navigator.clipboard.writeText(installCmd); toast.success("Copied"); }}
        >
          Copy
        </button>
      </div>
      {newToken && (
        <div className="mt-2 rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
          Copy this token now — it won't be shown again.
          <button
            className="ml-2 underline"
            onClick={() => {
              navigator.clipboard.writeText(newToken);
              toast.success("Token copied");
            }}
          >
            Copy
          </button>
        </div>
      )}

      <div className="mt-4 flex items-end gap-2">
        <label className="flex-1 text-sm">
          <span className="mb-1 block font-medium text-gray-700">
            New enrollment token
          </span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Production fleet"
            className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
          />
        </label>
        <Button onClick={create} disabled={creating}>
          Generate
        </Button>
      </div>

      {tokens.length > 0 && (
        <table className="mt-4 w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase text-gray-500">
              <th className="py-1.5">Name</th>
              <th className="py-1.5">Prefix</th>
              <th className="py-1.5">Last used</th>
              <th className="py-1.5"></th>
            </tr>
          </thead>
          <tbody>
            {tokens.map((t) => (
              <tr key={t.id} className="border-t border-gray-100">
                <td className="py-1.5">{t.name}</td>
                <td className="py-1.5 font-mono text-xs text-gray-500">
                  msrv_{t.prefix}…
                </td>
                <td className="py-1.5 text-xs text-gray-500">
                  {t.is_active ? relTime(t.last_used_at) : "revoked"}
                </td>
                <td className="py-1.5 text-right">
                  {t.is_active && (
                    <button
                      className="text-xs text-red-600 hover:underline"
                      onClick={() => revoke(t.id)}
                    >
                      Revoke
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

const _SEVERITY_COLOR = { critical: "text-red-700 bg-red-100", high: "text-orange-700 bg-orange-100", medium: "text-amber-700 bg-amber-100", low: "text-gray-600 bg-gray-100" };

function GlobalAlertRulesPanel({ refreshKey }) {
  const [rules, setRules] = useState(null);

  useEffect(() => {
    fetchServerAlertRules()
      .then((all) => setRules(all.filter((r) => r.server_id === null)))
      .catch(() => setRules([]));
  }, [refreshKey]);

  if (!rules || rules.length === 0) return null;

  async function remove(id) {
    if (!confirm("Delete this global alert rule?")) return;
    try {
      await deleteServerAlertRule(id);
      setRules((prev) => prev.filter((r) => r.id !== id));
      toast.success("Rule deleted");
    } catch {
      toast.error("Delete failed");
    }
  }

  return (
    <Card>
      <Title className="text-sm">Global alert rules</Title>
      <Text className="mt-0.5 text-xs text-gray-500">Apply to all servers in this workspace.</Text>
      <ul className="mt-3 space-y-1">
        {rules.map((r) => (
          <li key={r.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2 text-xs">
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="font-medium text-gray-800">{r.name}</span>
              {r.is_breaching && (
                <span className="rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold text-red-700">Firing</span>
              )}
              <span className="font-mono text-gray-500">{r.metric} {r.comparison === "lt" ? "<" : ">"} {r.threshold}</span>
              {r.duration_seconds > 0 && (
                <span className="text-gray-400">for {Math.round(r.duration_seconds / 60)}m</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium capitalize ${_SEVERITY_COLOR[r.severity] || "text-gray-600 bg-gray-100"}`}>
                {r.severity}
              </span>
              <button className="text-xs text-red-600 hover:underline" onClick={() => remove(r.id)}>
                Delete
              </button>
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}

export default function Servers() {
  const [servers, setServers] = useState([]);
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [rulesKey, setRulesKey] = useState(0);

  function load() {
    setLoading(true);
    setRulesKey((k) => k + 1);
    Promise.all([fetchServers(), fetchServerTokens()])
      .then(([s, t]) => {
        setServers(s);
        setTokens(t);
      })
      .catch((e) => toast.error(e?.response?.data?.error || "Load failed"))
      .finally(() => setLoading(false));
  }

  useEffect(load, []);

  const filteredServers = servers
    .filter((s) => {
      if (query) {
        const q = query.toLowerCase();
        const name = (s.display_name || s.hostname || "").toLowerCase();
        const host = (s.hostname || "").toLowerCase();
        if (!name.includes(q) && !host.includes(q)) return false;
      }
      if (statusFilter === "online") return s.is_active && isOnline(s.last_seen_at);
      if (statusFilter === "offline") return s.is_active && !isOnline(s.last_seen_at);
      if (statusFilter === "alerting") return s.open_incidents > 0;
      if (statusFilter === "paused") return !s.is_active;
      return true;
    })
    .sort((a, b) => {
      // Alerting first, then offline, then online, then paused.
      function rank(s) {
        if (s.open_incidents > 0) return 0;
        if (s.is_active && !isOnline(s.last_seen_at)) return 1;
        if (s.is_active) return 2;
        return 3;
      }
      const dr = rank(a) - rank(b);
      if (dr !== 0) return dr;
      return (a.display_name || "").localeCompare(b.display_name || "");
    });

  return (
    <div className="space-y-4">
      <Title>Servers</Title>

      <InstallPanel tokens={tokens} onChange={load} />

      <GlobalAlertRulesPanel refreshKey={rulesKey} />

      {!loading && servers.length > 0 && (
        <div className="flex flex-wrap gap-2">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter by name…"
            className="rounded border border-gray-300 px-2.5 py-1.5 text-sm w-48"
          />
          {["all", "online", "offline", "alerting", "paused"].map((f) => (
            <button
              key={f}
              onClick={() => setStatusFilter(f)}
              className={`rounded-full px-3 py-1 text-xs capitalize ${statusFilter === f ? "bg-teal-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
            >
              {f}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : servers.length === 0 ? (
        <Card>
          <Text>
            No servers reporting yet. Install the agent above and metrics will
            appear within a minute.
          </Text>
        </Card>
      ) : (
        <>
        {(() => {
          const online = servers.filter((s) => s.is_active && isOnline(s.last_seen_at)).length;
          const offline = servers.filter((s) => s.is_active && !isOnline(s.last_seen_at)).length;
          const paused = servers.filter((s) => !s.is_active).length;
          const alerting = servers.filter((s) => s.open_incidents > 0).length;
          return (
            <div className="flex flex-wrap gap-3 text-xs">
              <span className="flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-emerald-700">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                {online} online
              </span>
              {offline > 0 && (
                <span className="flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1 text-gray-600">
                  <span className="h-1.5 w-1.5 rounded-full bg-gray-400" />
                  {offline} offline
                </span>
              )}
              {paused > 0 && (
                <span className="rounded-full bg-gray-100 px-3 py-1 text-gray-500">
                  {paused} paused
                </span>
              )}
              {alerting > 0 && (
                <span className="rounded-full bg-red-100 px-3 py-1 font-medium text-red-700">
                  {alerting} alerting
                </span>
              )}
            </div>
          );
        })()}
        {filteredServers.length === 0 && (
          <p className="text-sm text-gray-500">No servers match the current filter.</p>
        )}
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
          {filteredServers.map((s) => (
            <Link key={s.id} to={`/servers/${s.id}`}>
              <Card className={`transition hover:ring-2 hover:ring-teal-200 ${!s.is_active ? "opacity-60" : ""}`}>
                <div className="mb-2 flex items-center gap-2">
                  <ServerIcon className="h-4 w-4 text-gray-500" />
                  <span className="truncate font-semibold text-gray-900">
                    {s.display_name}
                  </span>
                  <div className="ml-auto flex items-center gap-1.5">
                    {s.open_incidents > 0 && (
                      <Link
                        to={`/incidents?server_id=${s.id}`}
                        onClick={(e) => e.stopPropagation()}
                        className="text-[10px] font-semibold text-red-700 bg-red-100 rounded px-1.5 py-0.5 hover:bg-red-200"
                      >
                        {s.open_incidents} alert{s.open_incidents > 1 ? "s" : ""}
                      </Link>
                    )}
                    {!s.is_active ? (
                      <span className="text-[10px] font-medium text-gray-400 bg-gray-100 rounded px-1.5 py-0.5">
                        Paused
                      </span>
                    ) : (
                      <span
                        className={`h-2 w-2 rounded-full ${
                          isOnline(s.last_seen_at) ? "bg-emerald-500" : "bg-gray-300"
                        }`}
                        title={`Last seen ${relTime(s.last_seen_at)}`}
                      />
                    )}
                  </div>
                </div>
                <p className="mb-3 truncate text-xs text-gray-400">{s.os || "—"}</p>
                <div className="space-y-2">
                  <MetricLine
                    Icon={Cpu}
                    label="CPU"
                    value={fmtPct(s.latest?.cpu_pct)}
                    pct={s.latest?.cpu_pct}
                  />
                  <MetricLine
                    Icon={MemoryStick}
                    label="Mem"
                    value={fmtPct(s.latest?.mem_pct)}
                    pct={s.latest?.mem_pct}
                  />
                  <MetricLine
                    Icon={HardDrive}
                    label="Disk"
                    value={fmtPct(s.latest?.disk_pct)}
                    pct={s.latest?.disk_pct}
                  />
                  {s.latest?.load1 != null && (
                    <MetricLine
                      Icon={Activity}
                      label="Load"
                      value={s.latest.load1.toFixed(2)}
                      pct={Math.min(100, (s.latest.load1 / 4) * 100)}
                      tone="indigo"
                    />
                  )}
                </div>
                {s.latest && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {s.latest.disk_pct >= 90 && (
                      <span className="rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-medium text-red-700">
                        Disk {Math.round(s.latest.disk_pct)}%
                      </span>
                    )}
                    {s.latest.mem_pct >= 90 && (
                      <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
                        Mem {Math.round(s.latest.mem_pct)}%
                      </span>
                    )}
                    {s.latest.cpu_pct >= 90 && (
                      <span className="rounded bg-orange-100 px-1.5 py-0.5 text-[10px] font-medium text-orange-700">
                        CPU {Math.round(s.latest.cpu_pct)}%
                      </span>
                    )}
                    {s.latest.uptime_seconds != null && s.latest.uptime_seconds < 3600 && (
                      <span className="rounded bg-sky-100 px-1.5 py-0.5 text-[10px] font-medium text-sky-700">
                        Rebooted
                      </span>
                    )}
                  </div>
                )}
                {(s.stats_24h?.p95_cpu_pct != null || s.latest?.uptime_seconds != null) && (
                  <p className="mt-1.5 text-[10px] text-gray-400">
                    {s.stats_24h?.p95_cpu_pct != null && (
                      <>CPU p95 {s.stats_24h.p95_cpu_pct}%{s.stats_24h.p95_mem_pct != null ? ` · mem p95 ${s.stats_24h.p95_mem_pct}%` : ""}</>
                    )}
                    {s.latest?.uptime_seconds != null && s.stats_24h?.p95_cpu_pct != null && " · "}
                    {fmtUptime(s.latest?.uptime_seconds)}
                  </p>
                )}
              </Card>
            </Link>
          ))}
        </div>
        </>
      )}
    </div>
  );
}

function MetricLine({ Icon, label, value, pct, tone }) {
  return (
    <div>
      <div className="mb-0.5 flex items-center gap-1.5 text-xs text-gray-600">
        <Icon className="h-3.5 w-3.5" />
        <span>{label}</span>
        <span className="ml-auto font-medium">{value}</span>
      </div>
      <Bar value={pct} tone={tone} />
    </div>
  );
}
